import { FunctionComponent } from "react";
import { useBooks } from "../../features/HomePage/api/client";
import { Paginate } from "../../ui/components/Pagination/Pagination";
import { usePageParam } from "../../hooks/usePageParam";
import { getPagesCount } from "../../utilities/getPagesCount";
import { Box, Center, Stack, Text } from "@chakra-ui/layout";
import Breadcrumb from "../../features/CategoriesPage/components/Breadcrumb/Breadcrumb";
import ErrorMessage from "../../ui/components/ErrorMessage/ErrorMessage";
import BookList from "../../features/SelectedCategoryPage/components/BookList/BookList";
import Loader from "../../ui/components/Loader/Loader";
import { wordExchanger } from "../../utilities/wordExchanger";
import Seo from "../../ui/components/Seo/Seo";

interface BooksPageProps {
  type: "bestsellerBooks" | "newcomingBooks";
  title: string;
}

const BooksPage: FunctionComponent<BooksPageProps> = ({ type, title }) => {
  const currentPage = usePageParam();

  const { data: bookList, isLoading, error } = useBooks(type, currentPage, 15);

  const pageCount = getPagesCount(bookList?.paging);

  if (error) {
    return <ErrorMessage />;
  }
  if (bookList?.books.length === 0) {
    return (
      <Center paddingTop="3rem" height={{ base: "50vh", sm: "fit-content" }}>
        <Text
          opacity={0.5}
          size={{ base: "md", sm: "xs", md: "md", xl: "lg" }}
          fontWeight={500}
        >
          На даний момент книжок немає
        </Text>
      </Center>
    );
  }

  return (
    <>
      <Seo
        description={
          type == "bestsellerBooks"
            ? "Бетселери в Читанці"
            : "Новинки в Читанці"
        }
        title={
          type == "bestsellerBooks"
            ? "Бетселери в Читанці"
            : "Новинки в Читанці"
        }
      />
      <Box
        paddingTop={{
          base: "0rem",
          sm: "1.25rem",
          md: "1.75rem",
          xl: "2.5rem",
        }}
        px={{
          base: "0.5rem",
          md: "1rem",
          xl: "2rem",
          "2xl": "4.75rem",
        }}
      >
        <Breadcrumb
          breadcrumbItems={[
            { title: "Головна", path: "/" },
            { title: title ?? "" },
          ]}
        />
        <Box
          marginBottom={{
            base: "2rem",
            sm: "1.25rem",
            md: "2rem",
            "2xl": "3rem",
          }}
          marginTop={{
            base: "1rem",
            sm: "0.75rem",
            md: "1.75rem",
            xl: "2.5rem",
          }}
        >
          <Stack
            justifyContent="space-between"
            flexDirection={{ base: "column", sm: "row" }}
            alignItems={{ base: "flex-start", sm: "flex-end" }}
            gap={{ base: "0.5rem", sm: "1rem", xl: "2rem" }}
            marginBottom={{ base: "1rem", md: "1.5rem", xl: "2rem" }}
          >
            <Text
              fontWeight={600}
              as="h1"
              lineHeight="1"
              fontSize={{
                base: "1.75rem",
                sm: "1.5rem",
                md: "1,75rem",
                xl: "2.25rem",
              }}
              variant="primary"
            >
              {title}
            </Text>
            <Text
              marginTop="0rem !important"
              fontWeight={400}
              lineHeight="1rem"
              variant="secondary"
              size={{ base: "caption", sm: "xs", md: "sm", xl: "md" }}
            >
              {bookList &&
                `${bookList?.paging.totalElements} ${wordExchanger(
                  bookList?.paging.totalElements
                )}`}
            </Text>
          </Stack>
        </Box>
        {isLoading ? (
          <Loader width="100%" />
        ) : (
          <BookList books={bookList?.books ?? []} />
        )}
        <Paginate pageCount={pageCount} currentPage={currentPage} />
      </Box>
    </>
  );
};

export default BooksPage;
